import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { track } from './track';
import { TryAskingVela } from './meetVela/TryAskingVela';
import { VelaIntroVideo } from './meetVela/VelaIntroVideo';

// Signed-in visitors go where Vela already lives for them (routes verified in App.js / LoginPage.js);
// everyone else enters the existing free-trial flow (same destination as the hero's trial button).
const destinationFor = (user) => {
  if (!user) return '/pricing';
  if (String(user.plan_level || '').startsWith('partner_')) return '/partner';
  return user.has_onboarded ? '/home' : '/onboarding';
};

// Reveal once when the section scrolls into view (CSS handles the stagger via --d).
const useInView = (ref) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) { setOn(true); return undefined; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setOn(true); io.disconnect(); } }, { rootMargin: '-80px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return on;
};

const Reveal = ({ d = 0, className = '', children, ...rest }) => (
  <div className={`mv-reveal ${className}`} style={{ '--d': `${d}ms` }} {...rest}>{children}</div>
);

export const MeetVela = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRef(null);
  const inView = useInView(ref);
  const meet = () => { track('meet_vela_cta_clicked', { signed_in: !!user }); navigate(destinationFor(user)); };

  return (
    <section ref={ref} id="meet-vela" data-testid="meet-vela-section" data-inview={inView}
      aria-labelledby="meet-vela-headline"
      className="relative overflow-hidden border-y border-white/[0.06] bg-[#06060a] px-5 sm:px-8 py-24 sm:py-32 lg:py-36">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-px w-[70%] -translate-x-1/2 bg-gradient-to-r from-transparent via-fuchsia-400/40 to-transparent" />
        <div className="absolute -top-40 left-[8%] h-[420px] w-[520px] rounded-full bg-violet-600/[0.10] blur-[140px]" />
        <div className="absolute -bottom-48 right-[4%] h-[460px] w-[560px] rounded-full bg-fuchsia-600/[0.08] blur-[150px]" />
      </div>

      <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-x-14 gap-y-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-center
                      [grid-template-areas:'copy''video''ask''cta'] lg:[grid-template-areas:'copy_video''ask_video''cta_video']">
        <div className="[grid-area:copy] lg:self-end">
          <Reveal d={0} className="mb-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2.5">
              <span className="mv-presence h-2 w-2 rounded-full bg-fuchsia-400" aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.26em] text-fuchsia-300" data-testid="meet-vela-eyebrow">Meet Vela</span>
            </span>
            <span data-testid="meet-vela-now-live" className="inline-flex items-center rounded-full border border-emerald-400/25 bg-emerald-500/[0.08] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200">Now live</span>
          </Reveal>
          <Reveal d={80}>
            <h2 id="meet-vela-headline" data-testid="meet-vela-headline"
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl font-black tracking-tighter leading-[1.04] text-white">
              <span className="block [text-wrap:balance]">You have big plans.</span>
              <span className="block [text-wrap:balance]"><span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 via-fuchsia-300 to-fuchsia-200">Vela</span> can’t wait to get started.</span>
            </h2>
          </Reveal>
          <Reveal d={160}>
            <p data-testid="meet-vela-description" className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-zinc-400">
              Your AI growth agent is already inside Veluro. Tell her your goals—she’ll help create, launch, and optimize the marketing that moves your business forward.
            </p>
          </Reveal>
        </div>

        <Reveal d={140} className="[grid-area:video] lg:self-center">
          <VelaIntroVideo />
          <p data-testid="meet-vela-greeting" className="mt-6 flex items-center justify-center gap-2.5 text-center text-sm sm:text-[15px] text-white/60">
            <img src="/vela/face.webp" alt="" width={24} height={24} loading="lazy" className="h-6 w-6 rounded-full ring-1 ring-white/10" />
            <span>“Hi, I’m Vela. What would you like to accomplish?”</span>
          </p>
        </Reveal>

        <Reveal d={240} className="[grid-area:ask] lg:self-start">
          <TryAskingVela />
        </Reveal>

        <Reveal d={320} className="[grid-area:cta] lg:self-start">
          <p data-testid="meet-vela-capabilities" className="text-sm sm:text-[15px] font-semibold tracking-tight text-white/75">
            Ads. Social. Funnels. Follow-up. <span className="text-white/45 font-medium">All working together.</span>
          </p>
          <div className="mt-6">
            <button type="button" onClick={meet} data-testid="meet-vela-cta"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 px-8 py-4 text-base font-bold text-white shadow-2xl shadow-violet-500/30 transition-[transform,filter,box-shadow] hover:scale-[1.03] hover:brightness-110 hover:shadow-fuchsia-500/30 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#06060a]">
              Meet Vela <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </button>
            <p data-testid="meet-vela-fineprint" className="mt-4 text-sm text-white/40">
              Included inside Veluro. Try free for 7 days — $0 today, cancel anytime.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
